import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

export type ThemeMode = 'light' | 'dark';
export type ThemePreference = 'light' | 'dark' | 'system';

interface ThemeModeContextValue {
  mode: ThemeMode;
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
}

const THEME_MODE_STORAGE_KEY = 'spectre-theme-mode';

const getSystemMode = (): ThemeMode =>
  window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

const getInitialPreference = (): ThemePreference => {
  let storedMode: string | null = null;
  try {
    storedMode = localStorage.getItem(THEME_MODE_STORAGE_KEY);
  } catch {
    return 'system';
  }

  if (storedMode === 'light' || storedMode === 'dark' || storedMode === 'system') {
    return storedMode;
  }

  // Migrate the previous label without disrupting existing saved preferences.
  if (storedMode === 'normal') {
    return 'light';
  }

  return storedMode === 'dark' ? 'dark' : 'system';
};

const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(undefined);

export const ThemeModeProvider = ({ children }: { children: ReactNode }) => {
  const [preference, setPreference] = useState<ThemePreference>(getInitialPreference);
  const [systemMode, setSystemMode] = useState<ThemeMode>(getSystemMode);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const updateSystemMode = () => setSystemMode(mediaQuery.matches ? 'dark' : 'light');

    mediaQuery.addEventListener('change', updateSystemMode);
    return () => mediaQuery.removeEventListener('change', updateSystemMode);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(THEME_MODE_STORAGE_KEY, preference);
    } catch {
      // Theme selection remains usable when browser storage is unavailable.
    }
  }, [preference]);

  const mode: ThemeMode = preference === 'system'
    ? systemMode
    : preference === 'light'
      ? 'light'
      : 'dark';

  return (
    <ThemeModeContext.Provider value={{ mode, preference, setPreference }}>
      {children}
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = () => {
  const context = useContext(ThemeModeContext);

  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider');
  }

  return context;
};